import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'

import { prepareDefaultHeaders } from '../../api'
import {
  AiImagePromptVersionResponseDTO,
  AiPromptDefinitionResponseDTO,
  AiTextPromptVersionResponseDTO,
  CreateImageDefinitionRequest,
  CreateImageVersionRequest,
  CreateTextDefinitionRequest,
  CreateTextVersionRequest,
  UpdateDefinitionRequest,
  UpdateImageVersionRequest,
  UpdateTextVersionRequest,
} from '../features/ai-prompts/types/types'

const REDUCER_PATH = 'aiPromptsApi'

export const aiPromptsApi = createApi({
  reducerPath: REDUCER_PATH,
  baseQuery: fetchBaseQuery({
    baseUrl: window.GR_API_URLS.API_URL_LIVE_EVENTS_PREDICTIONS + '/i/prompts/',
    prepareHeaders: prepareDefaultHeaders,
  }),
  tagTypes: ['TextDefinitions', 'TextDefinition', 'ImageDefinitions', 'ImageDefinition', 'TextVersions', 'ImageVersions'],
  endpoints: (builder) => ({
    // Text definitions
    listTextDefinitions: builder.query<AiPromptDefinitionResponseDTO[], void>({
      query: () => 'text/definitions',
      providesTags: ['TextDefinitions'],
    }),
    getTextDefinition: builder.query<AiPromptDefinitionResponseDTO, string>({
      query: (name) => `text/definitions/${name}`,
      providesTags: (_result, _error, name) => [{ type: 'TextDefinition', id: name }],
    }),
    createTextDefinition: builder.mutation<AiPromptDefinitionResponseDTO, CreateTextDefinitionRequest>({
      query: (body) => ({ url: 'text/definitions', method: 'POST', body }),
      invalidatesTags: ['TextDefinitions'],
    }),
    updateTextDefinition: builder.mutation<AiPromptDefinitionResponseDTO, { name: string; body: UpdateDefinitionRequest }>({
      query: ({ name, body }) => ({ url: `text/definitions/${name}`, method: 'PUT', body }),
      invalidatesTags: (_result, _error, { name }) => ['TextDefinitions', { type: 'TextDefinition', id: name }],
    }),
    deleteTextDefinition: builder.mutation<void, string>({
      query: (name) => ({ url: `text/definitions/${name}`, method: 'DELETE' }),
      invalidatesTags: ['TextDefinitions'],
    }),

    // Text versions
    getTextVersions: builder.query<AiTextPromptVersionResponseDTO[], string>({
      query: (name) => `text/definitions/${name}/versions`,
      providesTags: (_result, _error, name) => [{ type: 'TextVersions', id: name }],
    }),
    getTextVersion: builder.query<AiTextPromptVersionResponseDTO, { name: string; version: number }>({
      query: ({ name, version }) => `text/definitions/${name}/versions/${version}`,
    }),
    createTextVersion: builder.mutation<AiTextPromptVersionResponseDTO, { name: string; body: CreateTextVersionRequest }>({
      query: ({ name, body }) => ({ url: `text/definitions/${name}/versions`, method: 'POST', body }),
      invalidatesTags: (_result, _error, { name }) => [
        { type: 'TextVersions', id: name },
        { type: 'TextDefinition', id: name },
      ],
    }),
    updateTextVersion: builder.mutation<AiTextPromptVersionResponseDTO, { name: string; version: number; body: UpdateTextVersionRequest }>({
      query: ({ name, version, body }) => ({ url: `text/definitions/${name}/versions/${version}`, method: 'PUT', body }),
      invalidatesTags: (_result, _error, { name }) => [
        { type: 'TextVersions', id: name },
        { type: 'TextDefinition', id: name },
      ],
    }),
    deleteTextVersion: builder.mutation<void, { name: string; version: number }>({
      query: ({ name, version }) => ({ url: `text/definitions/${name}/versions/${version}`, method: 'DELETE' }),
      invalidatesTags: (_result, _error, { name }) => [
        { type: 'TextVersions', id: name },
        { type: 'TextDefinition', id: name },
      ],
    }),

    // Image definitions
    listImageDefinitions: builder.query<AiPromptDefinitionResponseDTO[], void>({
      query: () => 'image/definitions',
      providesTags: ['ImageDefinitions'],
    }),
    getImageDefinition: builder.query<AiPromptDefinitionResponseDTO, string>({
      query: (name) => `image/definitions/${name}`,
      providesTags: (_result, _error, name) => [{ type: 'ImageDefinition', id: name }],
    }),
    createImageDefinition: builder.mutation<AiPromptDefinitionResponseDTO, CreateImageDefinitionRequest>({
      query: (body) => ({ url: 'image/definitions', method: 'POST', body }),
      invalidatesTags: ['ImageDefinitions'],
    }),
    updateImageDefinition: builder.mutation<AiPromptDefinitionResponseDTO, { name: string; body: UpdateDefinitionRequest }>({
      query: ({ name, body }) => ({ url: `image/definitions/${name}`, method: 'PUT', body }),
      invalidatesTags: (_result, _error, { name }) => ['ImageDefinitions', { type: 'ImageDefinition', id: name }],
    }),
    deleteImageDefinition: builder.mutation<void, string>({
      query: (name) => ({ url: `image/definitions/${name}`, method: 'DELETE' }),
      invalidatesTags: ['ImageDefinitions'],
    }),

    // Image versions
    getImageVersions: builder.query<AiImagePromptVersionResponseDTO[], string>({
      query: (name) => `image/definitions/${name}/versions`,
      providesTags: (_result, _error, name) => [{ type: 'ImageVersions', id: name }],
    }),
    getImageVersion: builder.query<AiImagePromptVersionResponseDTO, { name: string; version: number }>({
      query: ({ name, version }) => `image/definitions/${name}/versions/${version}`,
    }),
    createImageVersion: builder.mutation<AiImagePromptVersionResponseDTO, { name: string; body: CreateImageVersionRequest }>({
      query: ({ name, body }) => ({ url: `image/definitions/${name}/versions`, method: 'POST', body }),
      invalidatesTags: (_result, _error, { name }) => [
        { type: 'ImageVersions', id: name },
        { type: 'ImageDefinition', id: name },
      ],
    }),
    updateImageVersion: builder.mutation<AiImagePromptVersionResponseDTO, { name: string; version: number; body: UpdateImageVersionRequest }>({
      query: ({ name, version, body }) => ({ url: `image/definitions/${name}/versions/${version}`, method: 'PUT', body }),
      invalidatesTags: (_result, _error, { name }) => [
        { type: 'ImageVersions', id: name },
        { type: 'ImageDefinition', id: name },
      ],
    }),
    deleteImageVersion: builder.mutation<void, { name: string; version: number }>({
      query: ({ name, version }) => ({ url: `image/definitions/${name}/versions/${version}`, method: 'DELETE' }),
      invalidatesTags: (_result, _error, { name }) => [
        { type: 'ImageVersions', id: name },
        { type: 'ImageDefinition', id: name },
      ],
    }),
  }),
})

export const {
  useListTextDefinitionsQuery,
  useGetTextDefinitionQuery,
  useCreateTextDefinitionMutation,
  useUpdateTextDefinitionMutation,
  useDeleteTextDefinitionMutation,
  useGetTextVersionsQuery,
  useGetTextVersionQuery,
  useCreateTextVersionMutation,
  useUpdateTextVersionMutation,
  useDeleteTextVersionMutation,
  useListImageDefinitionsQuery,
  useGetImageDefinitionQuery,
  useCreateImageDefinitionMutation,
  useUpdateImageDefinitionMutation,
  useDeleteImageDefinitionMutation,
  useGetImageVersionsQuery,
  useGetImageVersionQuery,
  useCreateImageVersionMutation,
  useUpdateImageVersionMutation,
  useDeleteImageVersionMutation,
} = aiPromptsApi
